import React, { useState, useEffect, useRef } from 'react';
import { useAppDispatch } from '../hooks/reduxHooks';
import { useEscapeToClose } from '../hooks/useEscapeToClose';
import { renameProject } from '../ProjectSlice_Store';
import ChatModalFrame, { ChatModalFooter, ChatModalHeader } from './ChatModalFrame';

interface RenameChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  currentName: string;
}

function useRenameChat({ isOpen, onClose, projectId, currentName }: RenameChatModalProps) {
  const dispatch = useAppDispatch();
  const [name, setName] = useState(currentName);
  const [isSaving, setIsSaving] = useState(false);
  const prevOpenRef = useRef(false);

  // Reset input only on the open transition (isOpen goes false→true)
  useEffect(() => {
    if (isOpen && !prevOpenRef.current) {
      setName(currentName);
    }
    prevOpenRef.current = isOpen;
  }, [isOpen, currentName]);

  useEscapeToClose(isOpen, onClose, isSaving);

  const isSaveDisabled = !name.trim() || name.trim() === currentName || isSaving;

  const handleSave = async () => {
    if (isSaveDisabled) return;
    setIsSaving(true);
    try {
      await dispatch(renameProject({ projectId, name: name.trim() })).unwrap();
      onClose();
    } catch {
      // Keep modal open on error so user can retry
    } finally {
      setIsSaving(false);
    }
  };

  // Prevent closing while save is in-flight
  const handleClose = () => {
    if (!isSaving) {
      onClose();
    }
  };

  return { name, setName, isSaving, isSaveDisabled, save: () => void handleSave(), handleClose };
}

export default function RenameChatModal(props: RenameChatModalProps) {
  const { name, setName, isSaving, isSaveDisabled, save, handleClose } = useRenameChat(props);

  if (!props.isOpen) return null;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      save();
    }
  };

  return (
    <ChatModalFrame titleId="rename-chat-modal-title" onClose={handleClose}>
      <ChatModalHeader titleId="rename-chat-modal-title" title="Rename chat" />

      {/* Body */}
      <div className="px-6 py-4">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          placeholder="Chat name"
        />
      </div>

      <ChatModalFooter
        onCancel={handleClose}
        onConfirm={save}
        isBusy={isSaving}
        isConfirmDisabled={isSaveDisabled}
        labels={{ confirm: 'Save', busy: 'Saving...' }}
      />
    </ChatModalFrame>
  );
}
